<template>
  <div id="test-list" class="TableContainer tabulator">
    <div class="table-header">
      <div class="table-title-section">
        <i class="bi bi-list-ul title-icon"></i>
        <h3 class="table-title">Tests List</h3>
      </div>
      <div class="search-section">
        <i class="bi bi-search search-icon"></i>
        <input v-model="searchQuery" @input="onSearchInput" type="text" placeholder="Global search..."
          class="form-control search-input" />
      </div>
    </div>
    <div id="test-table" ref="test-table"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import api from "@/services/axios";
import { useTestStore } from "../stores/testStore";
import { useEventBus } from '@vueuse/core';
import { DateTime } from "luxon";
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { getUNESCOLabel } from '@/utils/unescoSubjects';

// Event bus pour écouter les mises à jour de tests
const testUpdateBus = useEventBus('test-updated');

window.DateTime = DateTime;

const store = useTestStore();

const editorMain = ref(null);

const searchQuery = ref("");

const onSearchInput = () => {
  if (table) {
    console.log("Applying filter with value:", searchQuery.value);
    table.setFilter([
      { field: "global", type: "like", value: searchQuery.value }
    ]);
    table.setPage(1); // revient à la première page

  }
};

const tests = ref([]);
let table = null;
let selectedRowId = null;

// Configuration conforme à Tabulator 6.x pour la pagination serveur
const fetchTestsForTabulator = async (url, config, params) => {

  const page = params.page || 1;
  const size = params.size || 10;

  // Récupération du filtre - correction ici
  let filterValue = "";

  // Dans Tabulator 6.3, les filtres sont accessibles via params.filters
  if (params.filters && Array.isArray(params.filters)) {
    const globalFilter = params.filters.find(f => f.field === "global");
    if (globalFilter) {
      filterValue = globalFilter.value || "";
    }
  }

  // Support pour l'ancien format également (au cas où)
  else if (Array.isArray(params.filter)) {
    const globalFilter = params.filter.find(f => f.field === "global");
    filterValue = globalFilter?.value || "";
  }

  // Récupération du tri
  let sortField = "";
  let sortDir = "";
  if (Array.isArray(params.sorters) && params.sorters.length > 0) {
    sortField = params.sorters[0].field;
    sortDir = params.sorters[0].dir;
  }

  let queryParams = `?page=${page}&size=${size}`;
  if (filterValue) queryParams += `&filter=${encodeURIComponent(filterValue)}`;
  if (sortField && sortDir) queryParams += `&sortField=${sortField}&sortDir=${sortDir}`;

  try {
    const response = await api.get(`/tests/getTests${queryParams}`);

    // Vérifier s'il y a des doublons dans les données
    const tests = response.data.tests || [];
    const duplicates = tests.filter((test, index, arr) =>
      arr.findIndex(t => t.title === test.title) !== index
    );

    if (duplicates.length > 0) {
      console.warn('🚨 Duplicates found in API response:', duplicates.map(t => ({
        title: t.title,
        ID: t.ID,
        _id: t._id,
        elementsCount: t.elements?.length || 0
      })));
    }

    return {
      data: tests,
      last_page: response.data.totalPages || 1,
      total_pages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0,
    };
  } catch (error) {
    console.error("Erreur ajaxRequestFunc :", error);
    return { data: [], last_page: 1, total_pages: 1, totalItems: 0 };
  }
};





// Gestion des actions
const handleAction = (e, cell) => {
  e.stopPropagation(); // Empêche le déclenchement de l'événement rowClick

  const row = cell.getRow();
  const rowData = row.getData();
  
  if (e.target.classList.contains("preview-btn")) {
    previewTest(rowData);
  
  } else if (e.target.classList.contains("edit-btn")) {
    emit("edit-test", rowData);
  
  } 
};

const emit = defineEmits(["edit-test"]);

// Fonction de prévisualisation
const previewTest = (test) => {
  alert(`Previewing Test: ${test.title}`);
};

// Fonction d'édition modifiée
// Dans TestList.vue, modifiez la fonction editTest

const editTest = async (test) => {
  try {
    selectedRowId = test.ID;

    // Reset et mise à jour du store avec protection
    store.resetState();

    // Créer une copie sécurisée du test
    const testCopy = {
      ...test,
      CATParameters: test.CATParameters || {
        paliers: 4,
        confirmation: 3,
        echec: 3,
        calculatedItems: 0,
        randomizedItemOrder: true
      }
    };

    store.setTestData(testCopy);

    // Mettre à jour uniquement le statut de surbrillance
    table.getRows().forEach((row) => {
      const rowData = row.getData();
      const isSelected = rowData.ID === test.ID;

      // Mettre à jour seulement si nécessaire pour éviter les effets de bord
      if (isSelected !== rowData.isHighlighted) {
        row.update({ isHighlighted: isSelected });
      }
    });

    // Émettre l'événement avec le type de test
    emit("edit-test", test);

    // Ajouter un défilement direct ici
    setTimeout(() => {
      const editorElement = document.getElementById("EditorMain");
      if (editorElement) {
        editorElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 200); // Délai plus long pour s'assurer que l'élément est rendu

  } catch (error) {
    console.error("Erreur lors de l'édition du test :", error);
  }
};

// Fonction de suppression
const deleteTest = async (test) => {
  console.log('🗑️ Attempting to delete test:', {
    title: test.title,
    ID: test.ID,
    _id: test._id,
    progression: test.progression
  });

  const testId = test.ID;

  if (!testId) {
    console.error('❌ No test ID found, cannot delete test');
    alert('Impossible de supprimer le test : ID manquant');
    return;
  }

  if (confirm(`Are you sure you want to delete Test: ${test.title}?`)) {
    try {
      console.log('🗑️ Deleting test with ID:', testId);
      // Suppression sur le backend
      const response = await api.delete(`/tests/${testId}`);

      // Suppression locale du store
      store.deleteTestById(testId);

      // Rafraîchir complètement les données depuis le serveur
      if (table) {
        console.log('🔄 Refreshing table data after test deletion...');
        table.replaceData().then(() => {
          // Émettre un événement pour notifier les autres composants
          console.log('✅ Test deleted and table refreshed, emitting refresh event:', test.title);
          testUpdateBus.emit('refresh-tests', {
            type: 'test-deleted',
            testId: testId,
            testData: test
          });
        }).catch((error) => {
          console.error('❌ Error refreshing table after deletion:', error);
          // En cas d'erreur, forcer un rafraîchissement complet
          setTimeout(() => {
            table.replaceData();
          }, 1000);
        });
      }
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
    }
  }
};

// Méthode pour ajouter un test
const addTest = async (newTest) => {
  try {
    console.log('➕ Adding test:', {
      title: newTest.title,
      ID: newTest.ID,
      _id: newTest._id,
      progression: newTest.progression
    });

    // Si le test a déjà un _id, cela signifie qu'il a déjà été créé (par exemple par CATNbQuestionsSimulator)
    // Dans ce cas, on ne refait pas la création, on rafraîchit juste la liste
    if (newTest._id) {
      console.log('✅ Test already created with _id:', newTest._id, '- refreshing list only');

      // Rafraîchir complètement les données depuis le serveur
      if (table) {
        console.log('🔄 Refreshing table data after test creation...');
        table.replaceData().then(() => {
          // Émettre un événement pour notifier les autres composants
          console.log('✅ Test already exists, table refreshed, emitting refresh event:', newTest.title);
          testUpdateBus.emit('refresh-tests', {
            type: 'test-created',
            testId: newTest._id || newTest.ID,
            testData: newTest
          });
        }).catch((error) => {
          console.error('❌ Error refreshing table after creation:', error);
          // En cas d'erreur, forcer un rafraîchissement complet
          setTimeout(() => {
            table.replaceData();
          }, 1000);
        });
      }
      return; // Sortir ici sans faire d'appel API
    }

    const response = await api.post(`/tests/addTest`, newTest);
    const savedTest = response.data;

    console.log('✅ Test saved:', {
      title: savedTest.title,
      ID: savedTest.ID,
      _id: savedTest._id,
      progression: savedTest.progression,
      elementsCount: savedTest.elements?.length || 0
    });

    // Rafraîchir complètement les données depuis le serveur
    if (table) {
      console.log('🔄 Refreshing table data after test creation...');
      table.replaceData().then(() => {
        // Émettre un événement pour notifier les autres composants
        console.log('✅ Test created and table refreshed, emitting refresh event:', savedTest.title);
        testUpdateBus.emit('refresh-tests', {
          type: 'test-created',
          testId: savedTest._id || savedTest.ID,
          testData: savedTest
        });
      }).catch((error) => {
        console.error('❌ Error refreshing table after creation:', error);
        // En cas d'erreur, forcer un rafraîchissement complet
        setTimeout(() => {
          table.replaceData();
        }, 1000);
      });
    }
  } catch (error) {
    console.error("❌ Erreur lors de l'ajout du test:", error);

    // Extraire le message d'erreur du backend
    let errorMessage = "Erreur lors de l'ajout du test.";

    if (error.response?.data) {
      const errorData = error.response.data;

      if (errorData.error) {
        errorMessage = errorData.error;

        // Ajouter la suggestion si disponible
        if (errorData.suggestion) {
          errorMessage += `\n\n💡 Suggestion: ${errorData.suggestion}`;
        }

        // Cas spécial pour conflit d'ID
        if (errorData.conflictReason === 'different_owner') {
          errorMessage += '\n\nℹ️ Ce test appartient à un autre utilisateur.';
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Afficher l'erreur à l'utilisateur via une notification
    alert(errorMessage);

    throw error; // Relancer pour que le parent puisse aussi gérer l'erreur
  }
};

// Méthode pour désélectionner la ligne en surbrillance
const clearSelection = () => {
  selectedRowId = null;

  if (table) {
    // Parcourir toutes les lignes et retirer la surbrillance
    table.getRows().forEach((row) => {
      row.update({ isHighlighted: false });
    });
  }

  console.log("Sélection de ligne effacée");
};

// Exposer les méthodes pour le composant parent
defineExpose({
  addTest,
  clearSelection,
});

// Initialisation de Tabulator avec la config pour v6.3.0
const initTable = () => {
  table = new Tabulator("#test-table", {
    pagination: true,
    paginationMode: "remote",
    paginationSize: 10,
    paginationSizeSelector: [10, 20, 50, 100],
    paginationCounter: "rows",
    ajaxURL: `/api/tests/getTests`,
    ajaxRequestFunc: fetchTestsForTabulator,
    ajaxFiltering: true,
    ajaxSorting: true,
    filterMode: "remote",
    layout: "fitColumns",
    columns: [
      {
        title: "Type",
        field: "progression",
        sorter: "string",
        width: 80,
        hozAlign: "center",
        formatter: function (cell) {
          const value = cell.getValue();
          if (value === "adaptive") {
            return '<span title="Adaptive CAT">🐈</span>';
          } else if (value === "conditional") {
            return '<span title="Conditional Branching">🔀</span>';
          } else {
            return '<span title="Linear">🚆</span>';
          }
        }
      },
      { title: "Title", field: "title", sorter: "string" },
      { title: "Creation Date", field: "metadata.Created", sorter: "date" },
      { title: "Elements", field: "elements.length", sorter: "number" },
      { title: "Last Modified", field: "metadata.LastModif", sorter: "date" },
      {
        title: "Subject",
        field: "Subject",
        sorter: "string",
        formatter: function (cell) {
          const value = cell.getValue();
          if (!value) return '';

          // Créer un span temporaire avec la valeur originale
          const span = document.createElement('span');
          span.textContent = value;
          span.style.color = '#6c757d';
          span.style.fontStyle = 'italic';

          // Convertir de façon asynchrone et mettre à jour le contenu
          getUNESCOLabel(value).then(label => {
            span.textContent = label;
            span.style.color = '';
            span.style.fontStyle = '';
          }).catch(error => {
            console.error('Error converting UNESCO code to label:', error);
            span.textContent = value; // Garder la valeur originale en cas d'erreur
            span.style.color = '#dc3545';
          });

          return span;
        }
      },
      { title: "ISCED", field: "ISCED", sorter: "string" },
      {
        title: "Actions",
        field: "actions",
        hozAlign: "center",
        headerSort: false,
        resizable: false,
        width: 80,
        formatter: (cell) => {
          const btn = document.createElement('button');
          btn.className = 'action-btn delete-btn';
          btn.title = 'Delete test';
          btn.innerHTML = '<i class="bi bi-trash3"></i>';

          btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            const rowData = cell.getRow().getData();
            deleteTest(rowData); // 👈 direct
          });

          return btn;
        },
        // IMPORTANT : retire ceci si présent
        // cellClick: handleAction,
      },
    ],
    rowFormatter: function (row) {
      const element = row.getElement();
      const data = row.getData();

      if (data.isHighlighted) {
        element.classList.add("row-highlight");
      } else {
        element.classList.remove("row-highlight");
      }
    },
    dataLoaded: function (data) {
      // Réappliquer la surbrillance après chargement des données
      if (selectedRowId) {
        setTimeout(() => {
          const rows = this.getRows();
          for (let row of rows) {
            const rowData = row.getData();
            if (rowData.ID === selectedRowId) {
              row.update({ isHighlighted: true });
              break;
            }
          }
        }, 10);
      }
    }
  });

  // Gestion de l'événement rowClick
  table.on("rowClick", function (e, row) {
    const data = row.getData();
    editTest(data);
  });

  // Gérer les états de chargement du tableau
  table.on("dataLoading", function () {
    isTableLoading = true;
    console.log('⏳ Table data loading started');
  });

  table.on("dataLoaded", function () {
    isTableLoading = false;
    console.log('✅ Table data loaded');
    // Traiter les mises à jour en attente
    processPendingUpdates();
  });

  table.on("dataLoadError", function () {
    isTableLoading = false;
    console.log('❌ Table data load error');
  });

  // Écouteurs d'événements supplémentaires pour la pagination
  table.on("pageLoaded", function () {
    // Vérifier si la ligne sélectionnée est sur cette page
    if (selectedRowId) {
      setTimeout(() => {
        const rows = table.getRows();
        for (let row of rows) {
          const rowData = row.getData();
          if (rowData.ID === selectedRowId) {
            row.update({ isHighlighted: true });
            break;
          }
        }
      }, 10);
    }
  });
};

// WATCHER TEMPORAIREMENT DÉSACTIVÉ POUR DEBUG
/*
// Watcher pour le store avec protection contre les boucles
let isUpdatingTable = false;
watch(
  () => store.testData,
  (newTestData) => {
    if (isUpdatingTable) return;
    if (!newTestData || !newTestData.ID) return;

    isUpdatingTable = true;
    
    // Mettre à jour spécifiquement la ligne dans la table si elle est visible
    if (table) {
      const rows = table.getRows();
      for (let row of rows) {
        const rowData = row.getData();
        if (rowData.ID === newTestData.ID) {
          // Ne mettre à jour que ce qui est nécessaire
          const updatedData = {
            ...rowData,
            ...newTestData,
            isHighlighted: true
          };
          row.update(updatedData);
          break;
        }
      }
    }
    
    setTimeout(() => { isUpdatingTable = false; }, 50);
  },
  { deep: true }
);
*/

// Flag pour éviter les conflits pendant le chargement
let isTableLoading = false;
let pendingUpdates = [];

// Fonction pour mettre à jour une ligne spécifique dans le tableau
const updateTableRow = (testId, updatedData) => {
  console.log('🔄 UpdateTableRow called with:', { testId, updatedData });

  if (!table) {
    console.log('❌ No table available');
    return;
  }

  // Si le tableau est en cours de chargement, mettre en file d'attente
  if (isTableLoading) {
    console.log('⏳ Table is loading, queuing update:', testId);
    pendingUpdates.push({ testId, updatedData });
    return;
  }

  console.log('🔄 Updating table row for test:', testId);

  try {
    const rows = table.getRows();
    console.log('📊 Total rows in table:', rows.length);

    let rowFound = false;
    for (let row of rows) {
      const rowData = row.getData();
      console.log('🔍 Checking row:', { rowId: rowData._id || rowData.ID, title: rowData.title });

      if (rowData._id === testId || rowData.ID === testId) {
        console.log('✅ Found matching row!');
        rowFound = true;

        // Mettre à jour les données de la ligne
        const newRowData = { ...rowData };

        // Gérer les différents types de mise à jour
        if (updatedData.newTitle) {
          console.log('📝 Updating title:', rowData.title, '→', updatedData.newTitle);
          newRowData.title = updatedData.newTitle;
        }
        if (updatedData.newDescription) {
          console.log('📝 Updating description:', rowData.Description, '→', updatedData.newDescription);
          newRowData.Description = updatedData.newDescription;
        }
        if (updatedData.changes) {
          if (updatedData.changes.title) {
            console.log('📝 Updating title from changes:', rowData.title, '→', updatedData.changes.title);
            newRowData.title = updatedData.changes.title;
          }
          if (updatedData.changes.Description) {
            console.log('📝 Updating description from changes:', rowData.Description, '→', updatedData.changes.Description);
            newRowData.Description = updatedData.changes.Description;
          }
          if (updatedData.changes.Subject) {
            console.log('📝 Updating subject from changes:', rowData.Subject, '→', updatedData.changes.Subject);
            newRowData.Subject = updatedData.changes.Subject;
          }
        }

        console.log('🔄 Applying row update:', newRowData);
        row.update(newRowData);
        console.log('✅ Table row updated successfully');
        break;
      }
    }

    if (!rowFound) {
      console.log('❌ Row not found in current page, trying refresh...');
      refreshTable(100);
    }

  } catch (error) {
    console.error('❌ Error updating table row:', error);
    // En cas d'erreur, programmer un rafraîchissement complet
    setTimeout(() => refreshTable(), 1000);
  }
};

// Fonction pour traiter les mises à jour en attente
const processPendingUpdates = () => {
  if (pendingUpdates.length > 0) {
    console.log('🔄 Processing pending updates:', pendingUpdates.length);
    const updates = [...pendingUpdates];
    pendingUpdates = [];

    updates.forEach(({ testId, updatedData }) => {
      updateTableRow(testId, updatedData);
    });
  }
};

// Fonction pour rafraîchir complètement le tableau avec délai si nécessaire
const refreshTable = (delay = 0) => {
  if (!table) return;

  const doRefresh = () => {
    if (isTableLoading) {
      console.log('⏳ Table is loading, retrying refresh in 500ms...');
      setTimeout(doRefresh, 500);
      return;
    }

    console.log('🔄 Refreshing table data...');
    try {
      table.replaceData();
      console.log('✅ Table refreshed');
    } catch (error) {
      console.error('❌ Error refreshing table:', error);
    }
  };

  if (delay > 0) {
    setTimeout(doRefresh, delay);
  } else {
    doRefresh();
  }
};

// Initialisation au montage
onMounted(() => {
  initTable();

  // Écouter les événements de mise à jour de test
  testUpdateBus.on((eventName, data) => {
    console.log('📡 TestList - Received event:', eventName, 'with data:', data);
    console.log('📡 TestList - Table state: isLoading =', isTableLoading, ', table exists =', !!table);

    // L'événement arrive avec le nom 'refresh-tests' et les données
    if (eventName === 'refresh-tests' && data && data.type) {
      if (data.type === 'title-updated' || data.type === 'description-updated' || data.type === 'title-description-updated' || data.type === 'subject-updated') {
        // Mise à jour spécifique de la ligne avec délai pour éviter les conflits
        console.log('📡 TestList - Scheduling row update in 100ms');
        setTimeout(() => {
          console.log('📡 TestList - Executing row update now');
          updateTableRow(data.testId, data);
        }, 100);
      } else {
        // Rafraîchissement complet pour d'autres types de mise à jour avec délai
        console.log('📡 TestList - Scheduling table refresh in 500ms');
        refreshTable(500);
      }
    } else {
      // Fallback: rafraîchissement complet si structure d'événement non reconnue
      console.log('📡 TestList - Unknown event structure, doing full refresh');
      refreshTable(500);
    }
  });

  // Optionnel: ajouter un écouteur pour le débogage
  if (table) {
    table.on("dataFiltering", function (filters) {
    });
  }
});
</script>

<style scoped>
#test-table {
  margin-top: 20px;
}

.TableContainer {
  width: 100%;
  height: auto;
  padding: 10px;
}

/* En-tête du tableau moderne */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #E0E0E0;
}

/* Section titre avec icône */
.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  font-size: 1.5rem;
  color: #1976D2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #E3F2FD;
  border-radius: 8px;
}

.table-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1565C0;
  margin: 0;
}

/* Section recherche */
.search-section {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 1rem;
  z-index: 1;
}

.search-input {
  padding-left: 40px;
  border-radius: 8px;
  border: 2px solid #E0E0E0;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #1976D2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-section {
    max-width: none;
  }

  .table-title {
    font-size: 1.2rem;
  }

  .title-icon {
    width: 35px;
    height: 35px;
    font-size: 1.3rem;
  }
}
</style>

<style>
/* En-têtes du tableau - Style moderne */
.tabulator .tabulator-header {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}

.tabulator .tabulator-header .tabulator-col {
  background: transparent;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.tabulator .tabulator-header .tabulator-col:last-child {
  border-right: none;
}

.tabulator .tabulator-header .tabulator-col-content {
  padding: 12px 10px;
}

.tabulator .tabulator-header .tabulator-col-title {
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Icônes de tri */
.tabulator .tabulator-header .tabulator-col .tabulator-arrow {
  border-bottom-color: white;
}

.tabulator .tabulator-header .tabulator-col.tabulator-sortable:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Bordures du tableau */
.tabulator {
  border: 2px solid #E0E0E0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Lignes du tableau */
.tabulator-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #F0F0F0;
}

.tabulator-row:hover {
  background-color: #F5F8FA !important;
}

.tabulator-row-even {
  background-color: #FAFBFC !important;
}

.tabulator-row-even:hover {
  background-color: #F5F8FA !important;
}

.row-highlight {
  background-color: #E3F2FD !important;
  font-weight: 600 !important;
  border-left: 4px solid #1976D2;
}

/* Cellules */
.tabulator-cell {
  padding: 12px 10px;
  border-right: 1px solid #F0F0F0;
}

/* Pagination */
.tabulator .tabulator-footer {
  background-color: #FAFBFC;
  border-top: 2px solid #E0E0E0;
  padding: 12px;
}

.tabulator .tabulator-footer .tabulator-page {
  background-color: white;
  border: 1px solid #E0E0E0;
  color: #424242;
  border-radius: 4px;
  margin: 0 2px;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

.tabulator .tabulator-footer .tabulator-page:hover {
  background-color: #E3F2FD;
  border-color: #1976D2;
  color: #1976D2;
}

.tabulator .tabulator-footer .tabulator-page.active {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
  color: white;
  border-color: #1976D2;
}

/* Bouton de suppression moderne et doux */
.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #6c757d;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background-color: #fff5f5;
  color: #dc3545;
  transform: scale(1.1);
}

.delete-btn:active {
  transform: scale(0.95);
}
</style>